import SectionCard from "@buildpy/ui/section-card";
import { formatDateTime } from "@buildpy/core/lib/format";
import { useSignedUrls } from "@/lib/signed-url";
import { cn } from "@/lib/utils";
import { isOwnMessage, MESSAGE_DELETED_LABEL, MESSAGE_EDITED_LABEL, MESSAGE_PHOTO_EXCERPT } from "@buildpy/core/modules/message/rules";
import type { MessageAuthorKind, MessageItem, MessageViewer } from "@buildpy/core/modules/message/types";
import MessageChanger, { type MessageChangeTarget } from "@/modules/message/ui/message-changer";

interface Props {
  messages: readonly MessageItem[];
  // 보는 쪽 — 내 쪽 글은 오른쪽, 상대 글은 왼쪽 카드.
  viewer: MessageAuthorKind;
  emptyText: string;
  testId: string;
  compact?: boolean;
  // 사진 주소 — 없으면 사진 글은 「(사진)」 글자만.
  photoPathOf?: (message: MessageItem) => string;
  // 고치기·지우기 — 직원은 자기 멤버 id 로 쓴 글만, 고객은 고객 글만(완료 현장이면 넘기지 않는다).
  change?: { target: MessageChangeTarget; who: MessageViewer };
}

const KIND_LABEL: Record<MessageAuthorKind, string> = { STAFF: "담당자", CUSTOMER: "고객" };

// 오래된 것부터 한 줄씩 — 본문은 줄바꿈 그대로(HTML 아님) · 지운 글은 흔적만 · 고친 글은 「수정됨」.
const MessageList = ({ messages, viewer, emptyText, testId, compact = false, photoPathOf, change }: Props) => {
  const srcOf = useSignedUrls(photoPathOf ? messages.filter((m) => m.has_photo).map(photoPathOf) : []);
  if (messages.length === 0) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={`${testId}-empty`}>
        {emptyText}
      </p>
    );
  }
  return (
    <ol className="flex flex-col gap-y-2" data-testid={testId}>
      {messages.map((m) => {
        const mine = m.author_kind === viewer;
        const deleted = m.deleted_at !== null;
        const src = m.has_photo && photoPathOf ? srcOf(photoPathOf(m)) : undefined;
        const head = (
          <p className="flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{m.author_name}</span>
            <span>{KIND_LABEL[m.author_kind]}</span>
            <span className="tabular-nums">{formatDateTime(m.created_at)}</span>
            {m.edited_at && !deleted && <span data-testid={`${testId}-edited`}>{MESSAGE_EDITED_LABEL}</span>}
          </p>
        );
        const content = deleted ? (
          <p className="mt-1 text-muted-foreground" data-testid={`${testId}-deleted`}>
            {MESSAGE_DELETED_LABEL}
          </p>
        ) : (
          <>
            {m.has_photo &&
              (photoPathOf && !compact ? (
                // 서명 주소가 오기 전엔 빈 링크·img 대신 자리만 잡는다.
                src === undefined ? (
                  <div
                    aria-label="대화 사진 불러오는 중"
                    className="mt-1 h-32 w-48 max-w-full animate-pulse rounded-md border border-border bg-muted"
                    data-testid={`${testId}-photo-pending`}
                    role="img"
                  />
                ) : (
                  <a className="mt-1 block" href={src} rel="noreferrer" target="_blank">
                    <img alt="대화 사진" className="max-h-64 w-auto rounded-md" data-testid={`${testId}-photo`} loading="lazy" src={src} />
                  </a>
                )
              ) : (
                <p className="mt-1 text-muted-foreground">{MESSAGE_PHOTO_EXCERPT}</p>
              ))}
            {m.body !== "" && <p className={cn("mt-1 whitespace-pre-wrap break-words", compact && "line-clamp-3")}>{m.body}</p>}
          </>
        );
        const controls =
          change && !deleted && isOwnMessage(m, change.who) ? (
            <MessageChanger key={`${m.id}:${m.edited_at ?? ""}`} message={m} target={change.target} testId={`${testId}-change`} />
          ) : null;
        return mine ? (
          <li
            key={m.id}
            className="ml-auto w-fit min-w-0 max-w-[85%] rounded-lg bg-muted px-3 py-2 text-sm"
            data-author={m.author_kind}>
            {head}
            {content}
            {controls}
          </li>
        ) : (
          <SectionCard
            key={m.id}
            as="li"
            className="mr-auto w-fit min-w-0 max-w-[85%] px-3 py-2 text-sm"
            data-author={m.author_kind}
            padding="none">
            {head}
            {content}
            {controls}
          </SectionCard>
        );
      })}
    </ol>
  );
};

export default MessageList;
